import React from 'react'
import clsx from 'clsx'
import { Messages2, MessageText, Sms, TickCircle } from 'iconsax-react'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'

import Content from './Content'
import OverflowText from '../../../../atoms/OverflowText'
import NewCount from '@SonarCX/components/molecules/BaseConversation/NewCount'

import { useQueueState } from '../../../../../providers/QueueStateProvider'
import { useAgent } from '../../../../../providers/AgentProvider'
import {
  MobileScreen,
  selectConvo,
  setMobileScreen,
  updateCache
} from '../../../../../slices/convo'
import { useNotificationCount } from '@SonarCX/providers/NotificationCountProvider'
import { useReadStatus } from '@SonarCX/providers/ReadStatusProvider'
import { selectViews } from '@SonarCX/slices/views'
import useStyles from './styles'

const OpenConversationV2 = ({
  conversation,
  disableInteract = false,
  showUnread = false
}) => {
  const dispatch = useAppDispatch()
  const { mobileScreen, view } = useAppSelector(selectConvo)
  const { id: selected, handleSelect } = useQueueState()
  const { updateCount } = useNotificationCount()
  const { convos } = useReadStatus()
  const { views } = useAppSelector(selectViews)

  const { currentUser } = useAgent()
  const { users, session, closed, verified, initiated_by, messages } =
    conversation

  const isViewing = session && sessionStorage.getItem('viewing') === session
  const isYours = users?.includes(currentUser()?.id) || isViewing
  const pickedUp = isYours

  const [hovering, setHovering] = React.useState(false)

  const isSelected = session === selected
  const classes = useStyles({ selected: isSelected, disableInteract })

  const handleOpenConversationClick = React.useCallback(() => {
    if (disableInteract) {
      return
    }

    handleSelect(conversation.session)
    if (mobileScreen === MobileScreen.EngagementHub) {
      dispatch(setMobileScreen(MobileScreen.Conversation))
    }
  }, [
    handleSelect,
    conversation?.session,
    mobileScreen,
    dispatch,
    disableInteract
  ])

  // Gets number of new messages
  const newCount = React.useMemo(() => {
    if (isSelected) {
      return 0
    }

    return updateCount(session, convos, messages)
  }, [isSelected, updateCount, session, convos, messages])

  // Store unreads in cache
  React.useEffect(() => {
    const count = showUnread ? newCount : 0
    const selectedView = views.find(v => v.id === view) ?? null
    dispatch(
      updateCache({
        id: session,
        count,
        userID: currentUser()?.id ?? '',
        view: selectedView
      })
    )
  }, [showUnread, newCount, session, currentUser, dispatch, views, view])

  const convoName = React.useMemo(() => {
    const isVerified = Boolean(verified?.stamp)

    const tick = (
      <TickCircle
        variant='Bold'
        size='16'
        color={isVerified ? '#06C270' : '#404040'}
        style={{ marginLeft: '4px' }}
      />
    )

    const iconElems = { integration: Messages2, email: Sms, sms: MessageText }

    let IconElem = Messages2
    if (initiated_by in iconElems) {
      IconElem = iconElems[initiated_by]
    }

    let channel = 'Chat'
    if (initiated_by === 'sms') {
      channel = 'Sms'
    } else if (initiated_by === 'email') {
      channel = 'Email'
    }
    const typeIcon = (
      <IconElem
        size='16'
        color={closed ? '#8F8F8F' : '#B64FCD'}
        variant='Bold'
      />
    )

    if (closed) {
      return (
        <span className={classes.convoTitle}>
          {typeIcon}
          <OverflowText>{channel} &#8226; Closed</OverflowText>
          {tick}
          {showUnread && <NewCount count={newCount} />}
        </span>
      )
    }

    return (
      <span className={clsx(classes.activeConvo, classes.convoTitle)}>
        {typeIcon}
        <OverflowText>
          {channel} &#8226; Active with<strong>you</strong>
        </OverflowText>
        {tick}
        {showUnread && <NewCount count={newCount} />}
      </span>
    )
  }, [
    classes.activeConvo,
    classes.convoTitle,
    closed,
    verified,
    initiated_by,
    newCount,
    showUnread
  ])

  return (
    <div className={clsx(classes.holder, 'holder')}>
      <div
        onClick={handleOpenConversationClick}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={classes.root}
      >
        <Content
          name={convoName}
          conversation={conversation}
          hovering={hovering}
          modal={false}
          pickedUp={pickedUp}
          showUnread={showUnread}
        />
      </div>
    </div>
  )
}

export default OpenConversationV2
